import { useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogBody, DialogFooter, DialogHeader } from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/cn"

// FeedbackButton — drop wherever an operator might want to flag bad AI
// output. Opens a small modal with category + textarea, posts to
// /operator_feedbacks (portal) or /admin/operator_feedbacks (admin),
// shows a thank-you toast. The submit fires a Rollbar.warning on the
// server so the team sees real-time signal.
//
// Pass whatever attribution IS in scope at the call site — the server
// derives the rest (e.g. test_run → test_scenario / test_batch / agent
// / company). Surface-specific extras go in `payload`.

export type FeedbackSurface =
  | "ticket_meta"
  | "agent_message"
  | "red_team_run"
  | "skill_test_lab"
  | "skill_designer"
  | "escalation_forge"
  | "topic_classifier"
  | "ally_dash"
  | "ai_cost_row"
  | "red_team_assertion"
  | "draft_from_ai"
  | "draft_red_team"

export type FeedbackCategory =
  | "general"
  | "wrong_answer"
  | "wrong_tool"
  | "wrong_topic"
  | "bad_tone"
  | "bug"
  | "suggestion"
  | "judge_wrong"

const CATEGORY_LABEL: Record<FeedbackCategory, string> = {
  general:      "General",
  wrong_answer: "Wrong answer",
  wrong_tool:   "Wrong tool / no tool",
  wrong_topic:  "Wrong topic tag",
  bad_tone:     "Bad tone",
  bug:          "Bug / crash",
  suggestion:   "Suggestion",
  judge_wrong:  "Judge call is wrong",
}

type Props = {
  surface:           FeedbackSurface
  agentId?:          number | null
  agentConvoId?:     number | null
  agentMessageId?:   number | null
  agentSkillId?:     number | null
  skillId?:          number | null
  skillVersionId?:   number | null
  testRunId?:        number | null
  testScenarioId?:   number | null
  testBatchId?:      number | null
  agentInvocationId?: number | null
  aiModelVersionId?: number | null
  companyId?:        number | null
  payload?:          Record<string, unknown>
  // Default category surfaced first in the dropdown — makes the killer
  // category one click away (e.g. judge_wrong on red_team_run pages).
  defaultCategory?:  FeedbackCategory
  label?:            string
  variant?:          "ghost" | "white" | "red" | "gold"
  size?:             "sm" | "md"
  // Where to POST. Defaults to portal endpoint; admin AA pages pass
  // "/admin/operator_feedbacks" instead.
  endpoint?:         string
  className?:        string
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ?? ""
}

export function FeedbackButton(props: Props) {
  const {
    surface, defaultCategory = "general",
    label = "Feedback",
    variant = "ghost", size = "sm",
    endpoint = "/operator_feedbacks",
    className,
    payload,
    ...attribution
  } = props

  const [open, setOpen]         = useState(false)
  const [category, setCategory] = useState<FeedbackCategory>(defaultCategory)
  const [body, setBody]         = useState("")
  const [submitting, setSub]    = useState(false)

  const close = () => {
    setOpen(false)
    setBody("")
    setCategory(defaultCategory)
  }

  const submit = async () => {
    if (body.trim().length < 4) return
    setSub(true)
    try {
      const res = await fetch(endpoint, {
        method:      "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept":       "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({
          surface,
          category,
          body: body.trim(),
          payload:           payload ?? {},
          url:               typeof window !== "undefined" ? window.location.href : null,
          agent_id:          attribution.agentId,
          agent_convo_id:    attribution.agentConvoId,
          agent_message_id:  attribution.agentMessageId,
          agent_skill_id:    attribution.agentSkillId,
          skill_id:          attribution.skillId,
          skill_version_id:  attribution.skillVersionId,
          test_run_id:       attribution.testRunId,
          test_scenario_id:  attribution.testScenarioId,
          test_batch_id:     attribution.testBatchId,
          agent_invocation_id: attribution.agentInvocationId,
          ai_model_version_id: attribution.aiModelVersionId,
          company_id:        attribution.companyId,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        toast.error((data?.errors && data.errors[0]) || "Could not save feedback.")
        return
      }
      toast.success("Thanks — we logged it.")
      close()
    } catch (e) {
      toast.error("Network error — try again.")
    } finally {
      setSub(false)
    }
  }

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className={cn(className)}
      >
        {label}
      </Button>
      <Dialog open={open} onClose={close} size="md">
        <DialogHeader
          title="Send feedback"
          subtitle="Tell us what's off about this AI output. Your message goes straight to the team — we read every one and use it to tune the model and prompts."
        />
        <DialogBody>
          <label className="block text-sm">
            <span className="mb-1 block text-app-muted">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
              className="w-full rounded-control border border-border bg-white/5 px-3 py-2 text-sm text-app-text"
            >
              {(Object.keys(CATEGORY_LABEL) as FeedbackCategory[]).map((k) => (
                <option key={k} value={k}>{CATEGORY_LABEL[k]}</option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-app-muted">What's wrong, and what did you expect?</span>
            <Textarea
              rows={6}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="The agent told the user their cashout would arrive same-day, but our policy is 1-3 business days."
            />
          </label>

          <p className="text-xs text-app-subtle">
            We capture surface, category, and the surrounding context (skill / convo / test / company / model)
            automatically — you don't need to repeat any IDs in the message.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={close} disabled={submitting}>Cancel</Button>
          <Button
            type="button"
            variant="red"
            onClick={submit}
            disabled={submitting || body.trim().length < 4}
          >
            {submitting ? "Sending…" : "Submit"}
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  )
}
